import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Plus, Trash2, PackageCheck, Upload, FileCheck2, FileText, ListChecks, Search } from "lucide-react";

export const fmtQty = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
export const STOCK_PO_STATUS = {
  draft: ["Draft", "bg-slate-100 text-slate-600"],
  requested: ["Requested From Finance", "bg-amber-100 text-amber-700"],
  approved: ["Approved · Awaiting PO", "bg-blue-100 text-blue-700"],
  rejected: ["Rejected By Finance", "bg-red-100 text-red-700"],
  in_transit: ["In Transit", "bg-indigo-100 text-indigo-700"],
  partial: ["Partly Received", "bg-sky-100 text-sky-700"],
  received: ["Received", "bg-emerald-100 text-emerald-700"],
  closed: ["Closed", "bg-slate-100 text-slate-500"],
};
const CURRENCIES = ["USD", "TZS", "EUR", "GBP", "ZAR", "AED", "CNY"];
const blankLine = { product_id: "", quantity: "", unit_price: "" };
const num = (v) => v.replace(/[^0-9.]/g, "");
const toB64 = (f) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(f); });
const MODES = [
  { k: "quote", label: "I Have The Supplier's Quote", icon: FileText },
  { k: "req", label: "From A Stock Requisition", icon: ListChecks },
  { k: "new_req", label: "Get Quotes First", icon: Search },
];

function SkuLines({ lines, setLines, skus, withPrice }) {
  const setLine = (i, k, v) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  return (
    <div className="space-y-2">
      <Label className="text-xs">Items</Label>
      {lines.map((l, i) => (
        <div key={i} className={`grid gap-2 ${withPrice ? "grid-cols-[1fr_70px_90px_auto]" : "grid-cols-[1fr_90px_auto]"}`} data-testid={`spo-line-${i}`}>
          <Select value={l.product_id} onValueChange={(v) => setLine(i, "product_id", v)}>
            <SelectTrigger className="min-w-0" data-testid={`spo-line-sku-${i}`}><SelectValue placeholder="SKU" /></SelectTrigger>
            <SelectContent>{skus.map((p) => <SelectItem key={p.id} value={p.id}>{p.sku} · {p.name}</SelectItem>)}</SelectContent>
          </Select>
          <Input inputMode="decimal" value={l.quantity} onChange={(e) => setLine(i, "quantity", num(e.target.value))} placeholder="Qty" data-testid={`spo-line-qty-${i}`} />
          {withPrice && <Input inputMode="decimal" value={l.unit_price} onChange={(e) => setLine(i, "unit_price", num(e.target.value))} placeholder="Price" data-testid={`spo-line-price-${i}`} />}
          <button type="button" onClick={() => setLines((ls) => (ls.length > 1 ? ls.filter((_, j) => j !== i) : ls))} className="px-1 text-slate-400 hover:text-red-600" data-testid={`spo-line-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={() => setLines((ls) => [...ls, blankLine])} data-testid="spo-add-line"><Plus className="mr-1.5 h-4 w-4" />Add Item</Button>
    </div>
  );
}

function RequisitionPicker({ pick, setPick }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/catalog/stock/requisitions").then(({ data }) => setRows(data)).catch((e) => toast.error(apiError(e))); }, []);
  const shown = (rows || []).filter((r) => !q || `${r.code} ${r.title}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-2" data-testid="spo-req-picker">
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search stock requisitions by code or title…" data-testid="spo-req-search" />
      <div className="max-h-64 space-y-1 overflow-y-auto rounded-md border border-border p-1.5">
        {rows === null ? <Loader2 className="mx-auto my-4 h-5 w-5 animate-spin text-muted-foreground" /> : !shown.length ? <p className="py-6 text-center text-xs text-muted-foreground">No stock requisitions yet — use "Get Quotes First".</p> : shown.map((r) => (
          <div key={r.id} className="rounded-md px-2 py-1.5 hover:bg-slate-50" data-testid={`spo-req-${r.id}`}>
            <div className="flex items-center gap-2 text-sm"><span className="font-mono text-xs font-semibold text-[#1B4383]">{r.code}</span><span className="truncate text-slate-800">{r.title}</span><span className="ml-auto text-[11px] text-muted-foreground">{r.items} item(s)</span></div>
            {!r.awarded.length ? <p className="pl-1 text-[11px] text-muted-foreground">No supplier awarded yet — open the requisition to send it out and award.</p> : r.awarded.map((a) => (
              <label key={a.supplier_id} className={`mt-1 flex items-center gap-2 rounded px-2 py-1 text-xs ${a.has_po ? "opacity-50" : "cursor-pointer hover:bg-[#EEF3FA]"}`}>
                <input type="radio" name="spo-req" disabled={a.has_po} checked={pick?.rfq_id === r.id && pick?.supplier_id === a.supplier_id} onChange={() => setPick({ rfq_id: r.id, supplier_id: a.supplier_id })} data-testid={`spo-req-pick-${r.id}-${a.supplier_id}`} />
                <span className="font-medium text-slate-700">{a.supplier_name}</span><span className="text-muted-foreground">· {a.lines} awarded line(s)</span>{a.has_po && <span className="ml-auto text-[10px] font-semibold text-slate-500">PO already created</span>}
              </label>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function QuoteUpload({ quote, setQuote }) {
  const pick = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) { toast.error("File must be under 15 MB"); return; }
    setQuote({ file: await toB64(f), filename: f.name, content_type: f.type || "application/octet-stream" });
  };
  return (
    <div className="flex items-center justify-between gap-2 rounded-lg border border-border bg-slate-50/60 p-3" data-testid="spo-quote-slot">
      <div className="min-w-0"><div className="text-xs font-semibold text-slate-700">Supplier's Quote{quote && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700"><FileCheck2 className="h-3 w-3" />Attached</span>}</div><div className="truncate text-[11px] text-muted-foreground">{quote ? quote.filename : "Attach it so Finance can review"}</div></div>
      <div className="flex items-center gap-1.5">
        {quote && <button onClick={() => setQuote(null)} className="text-slate-400 hover:text-red-600" data-testid="spo-quote-remove"><Trash2 className="h-3.5 w-3.5" /></button>}
        <label className="inline-flex h-8 cursor-pointer items-center gap-1 rounded-md border border-input bg-white px-2.5 text-xs font-medium text-slate-700 hover:bg-slate-50" data-testid="spo-quote-upload"><Upload className="h-3.5 w-3.5" />{quote ? "Replace" : "Upload"}<input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.xls,.xlsx,.doc,.docx" onChange={pick} /></label>
      </div>
    </div>
  );
}

export function StockPoRequestDialog({ open, onOpenChange }) {
  const nav = useNavigate();
  const [mode, setMode] = useState("quote");
  const [suppliers, setSuppliers] = useState([]);
  const [skus, setSkus] = useState([]);
  const [f, setF] = useState({});
  const [lines, setLines] = useState([blankLine]);
  const [quote, setQuote] = useState(null);
  const [pick, setPick] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setMode("quote"); setPick(null); setQuote(null); setLines([blankLine]);
    setF({ supplier_id: "", currency: "USD", ref: "", expected_date: "", notes: "", title: "", quote_deadline: "" });
    api.get("/suppliers").then(({ data }) => setSuppliers(data)).catch(() => {});
    api.get("/catalog/products").then(({ data }) => setSkus(data)).catch(() => {});
  }, [open]);
  const run = async (fn) => { setBusy(true); try { await fn(); onOpenChange(false); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); } };
  const submit = () => {
    const ls = lines.filter((l) => l.product_id && Number(l.quantity) > 0);
    if (mode === "req") {
      if (!pick) { toast.error("Tick the requisition and awarded supplier"); return; }
      run(async () => { const { data } = await api.post("/catalog/stock/orders/from-requisition", pick); toast.success("Stock PO created from the requisition"); nav(`/orders/som-po/${data.id}`); });
      return;
    }
    if (!ls.length) { toast.error("Add at least one SKU with a quantity"); return; }
    if (mode === "new_req") {
      run(async () => {
        const { data } = await api.post("/catalog/stock/requisitions", { title: f.title, quote_deadline: f.quote_deadline, lines: ls.map((l) => ({ product_id: l.product_id, quantity: Number(l.quantity) })) });
        toast.success(`${data.code} created — send it to suppliers from here`); nav(`/rfqs/${data.id}`);
      });
      return;
    }
    if (!f.supplier_id) { toast.error("Pick the supplier"); return; }
    run(async () => {
      const { data } = await api.post("/catalog/stock/orders", { supplier_id: f.supplier_id, currency: f.currency, ref: f.ref, expected_date: f.expected_date, notes: f.notes, lines: ls.map((l) => ({ product_id: l.product_id, quantity: Number(l.quantity), unit_price: Number(l.unit_price) || 0 })) });
      if (quote) await api.post(`/orders/stock/supplier-pos/${data.id}/attach`, { kind: "supplier_quote", mode: "upload", ...quote });
      toast.success("Stock PO created — review it, then request the PO from Finance"); nav(`/orders/som-po/${data.id}`);
    });
  };
  const cta = mode === "new_req" ? "Create Stock Requisition" : "Create Stock PO";
  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="stock-po-dialog">
        <DialogHeader><DialogTitle>New Stock PO</DialogTitle><DialogDescription>Buying for stock. Somcore Finance reviews and issues the PO; the items then show as In Transit on Stock Levels.</DialogDescription></DialogHeader>
        <div className="grid gap-1.5 sm:grid-cols-3" data-testid="spo-modes">
          {MODES.map((m) => (
            <button key={m.k} type="button" onClick={() => setMode(m.k)} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-xs font-semibold transition-colors ${mode === m.k ? "border-[#1B4383] bg-[#F5F8FD] text-[#1B4383]" : "border-border text-slate-600 hover:bg-slate-50"}`} data-testid={`spo-mode-${m.k}`}><m.icon className="h-4 w-4 shrink-0" />{m.label}</button>
          ))}
        </div>
        {mode === "quote" && (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs">Supplier *</Label>
                <Select value={f.supplier_id} onValueChange={(v) => setF({ ...f, supplier_id: v })}>
                  <SelectTrigger data-testid="spo-supplier"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                  <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Currency</Label>
                <Select value={f.currency} onValueChange={(v) => setF({ ...f, currency: v })}>
                  <SelectTrigger data-testid="spo-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Expected Arrival</Label><Input type="date" value={f.expected_date || ""} onChange={(e) => setF({ ...f, expected_date: e.target.value })} data-testid="spo-expected" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Supplier Quote Ref</Label><Input value={f.ref || ""} onChange={(e) => setF({ ...f, ref: e.target.value })} placeholder="Optional" data-testid="spo-ref" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Note</Label><Input value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="Optional" data-testid="spo-notes" /></div>
            </div>
            <QuoteUpload quote={quote} setQuote={setQuote} />
            <SkuLines lines={lines} setLines={setLines} skus={skus} withPrice />
          </>
        )}
        {mode === "req" && <RequisitionPicker pick={pick} setPick={setPick} />}
        {mode === "new_req" && (
          <>
            <p className="rounded-md bg-[#F5F8FD] px-3 py-2 text-xs text-[#1B4383]">Creates a stock requisition (SOM-REQ) you send to suppliers like a client RFQ. Once a supplier quotes and you award them, come back here and pick it under "From A Stock Requisition".</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5"><Label className="text-xs">Title</Label><Input value={f.title || ""} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="e.g. Bolts restock Q4 (optional)" data-testid="spo-req-title" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Quotes Needed By</Label><Input type="date" value={f.quote_deadline || ""} onChange={(e) => setF({ ...f, quote_deadline: e.target.value })} data-testid="spo-req-deadline" /></div>
            </div>
            <SkuLines lines={lines} setLines={setLines} skus={skus} />
          </>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#1B4383] hover:bg-[#16386e]" data-testid="spo-submit">{busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}{cta}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function StockReceiveDialog({ po, onOpenChange, onDone }) {
  const [locations, setLocations] = useState(null);
  const [loc, setLoc] = useState("");
  const [qty, setQty] = useState({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!po) return;
    setNote("");
    setQty(Object.fromEntries(po.lines.map((l) => [l.product_id, l.outstanding ? String(l.outstanding) : ""])));
    api.get("/catalog/stock/locations").then(({ data }) => { setLocations(data); setLoc(data[0]?.id || ""); }).catch(() => {});
  }, [po]);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/catalog/stock/orders/${po.id}/receive`, { location_id: loc, note, lines: Object.entries(qty).map(([product_id, q]) => ({ product_id, qty: Number(q) || 0 })) });
      toast.success("Stock received");
      onOpenChange(false); onDone?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!po} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="max-w-lg" data-testid="receive-dialog">
        <DialogHeader><DialogTitle>Receive Stock</DialogTitle><DialogDescription>Enter what actually arrived. Anything not received stays In Transit.</DialogDescription></DialogHeader>
        {locations && !locations.length && <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700">Add a stock location on the Stock Levels page first.</p>}
        <div className="space-y-1.5">
          <Label className="text-xs">Receive Into</Label>
          <Select value={loc} onValueChange={setLoc}>
            <SelectTrigger data-testid="receive-location"><SelectValue placeholder="Select location" /></SelectTrigger>
            <SelectContent>{(locations || []).map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          {po?.lines.filter((l) => l.outstanding > 0).map((l) => (
            <div key={l.product_id} className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2 text-xs">
              <div className="min-w-0"><div className="font-mono font-semibold text-[#1B4383]">{l.sku}</div><div className="truncate text-muted-foreground">{l.description} · {fmtQty(l.outstanding)} {l.uom} in transit</div></div>
              <Input inputMode="decimal" className="h-8 w-24" value={qty[l.product_id] ?? ""} onChange={(e) => setQty({ ...qty, [l.product_id]: num(e.target.value) })} data-testid={`receive-qty-${l.product_id}`} />
            </div>
          ))}
        </div>
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note, e.g. GRN 0042 or 2 cartons damaged" data-testid="receive-note" />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !loc} className="bg-[#527D63] hover:bg-[#456a54]" data-testid="receive-submit">{busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <PackageCheck className="mr-1.5 h-4 w-4" />}Receive</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
